import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import { trackEvent } from './analytics';
import { classifyDownloadPlatform } from './AppPage';

// Every mChatAI app on one page. The store apps link to their listings; the two
// Mac DMGs read their version and download URL from /downloads/manifest.json,
// which the release scripts (release-shell.sh, release-mchatai-direct.sh) write.

const storeApps = [
  {
    id: 'ios',
    device: 'iPhone and iPad',
    title: 'mChatAI',
    description: 'Chat, audio, walking tours and the Hub of community-made games and mini-apps.',
    requirement: 'iOS 26 or later',
    cta: 'Get it on the App Store',
    href: 'https://apps.apple.com/us/app/mchatai/id6445939409',
    details: '/ios-app',
    accent: '#8b5cf6',
  },
  {
    id: 'macos',
    device: 'Mac',
    title: 'mChatAI+',
    description: 'Chat with the AI models you choose, plus slides, tables, a screen recorder, automations and more.',
    requirement: 'macOS 15.5 or later',
    cta: 'Get it on the Mac App Store',
    href: 'https://apps.apple.com/us/app/mchatai/id6757826624?mt=12',
    details: '/macos-app',
    accent: '#3b82f6',
  },
  {
    id: 'android',
    device: 'Android',
    title: 'mChatAI',
    description: 'AI chat, audio, walking tours and the Hub of community-made games. Free models need no account to start.',
    requirement: null,
    cta: 'Get it on Google Play',
    href: 'https://play.google.com/store/apps/details?id=com.sevenhillsstudio.mchatai',
    details: '/android-app',
    accent: '#22c55e',
  },
];

const macDownloads = [
  {
    manifestKey: 'mchatai-plus-direct',
    title: 'mChatAI+ direct download',
    description: 'The same Mac app, downloaded from this site instead of the App Store. It updates itself, and it includes a few features App Store apps are not allowed to have, such as buying cloud tokens here.',
    requirement: 'macOS 15.5 or later',
    fallbackHref: 'https://storage.googleapis.com/mchatai-downloads/mChatAImac-direct.dmg',
    details: '/macos-direct',
  },
  {
    manifestKey: 'mchatai-shell',
    title: 'mChatAI Shell',
    description: 'A free menu-bar companion for mChatAI+. It runs the coding agents, build tools and local services that a sandboxed Mac app cannot run by itself.',
    requirement: 'macOS 26 or later',
    fallbackHref: 'https://storage.googleapis.com/mchatai-downloads/mChatAIShell.dmg',
    details: '/shell-app',
  },
];

const cardStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  boxSizing: 'border-box',
};

const buttonStyle = (accent) => ({
  display: 'inline-block', padding: '9px 18px', background: accent, color: '#fff',
  borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: '0.88rem',
});

const smallText = { color: '#9ca3af', fontSize: '0.78rem', margin: '10px 0 0' };

function versionLine(entry) {
  if (!entry) return null;
  const released = entry.releasedAt
    ? new Date(entry.releasedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;
  const sizeMb = entry.sizeBytes ? (entry.sizeBytes / (1024 * 1024)).toFixed(0) : null;
  return [
    `Version ${entry.version} (${entry.build})`,
    released,
    sizeMb ? `${sizeMb} MB` : null,
  ].filter(Boolean).join(' · ');
}

export default function DownloadsPage() {
  const [manifest, setManifest] = useState(null);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Download mChatAI for iPhone, Mac and Android';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    fetch('/downloads/manifest.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => setManifest(m && m.products ? m.products : null))
      .catch(() => {});
  }, []);

  const track = (href, manifestKey, ctaDownload) => () => trackEvent('download_click', {
    platform: classifyDownloadPlatform(href, ctaDownload, manifestKey),
    source_page: 'Downloads',
  });

  return (
    <div className="mp-page" style={{ background: '#fff' }}>
      <section style={{ textAlign: 'center', padding: '56px 24px 32px', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: '2.4rem', fontWeight: 800, color: '#111827', margin: '0 0 12px' }}>
          Download mChatAI
        </h1>
        <p style={{ fontSize: '1.05rem', color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
          Free on iPhone, iPad, Mac and Android. Pick your device.
        </p>
      </section>

      <section style={{ maxWidth: 960, margin: '0 auto 48px', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 20 }}>
          {storeApps.map((app) => (
            <article key={app.id} style={cardStyle}>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af', marginBottom: 4 }}>
                {app.device}
              </div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>{app.title}</h2>
              <p style={{ fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 18px', flex: 1 }}>
                {app.description}
              </p>
              <div>
                <a href={app.href} target="_blank" rel="noopener noreferrer" style={buttonStyle(app.accent)}
                  onClick={track(app.href, null, false)}>
                  {app.cta}
                </a>
              </div>
              <p style={smallText}>
                {app.requirement ? `${app.requirement} · ` : ''}
                <Link to={app.details} style={{ color: '#6b7280' }}>More about this app</Link>
              </p>
            </article>
          ))}
        </div>
      </section>

      <section style={{ maxWidth: 960, margin: '0 auto 56px', padding: '0 24px' }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>More for your Mac</h2>
        <p style={{ fontSize: '0.9rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 20px' }}>
          Most people only need the App Store version above. These two are downloaded from this site,
          and Apple notarizes every build.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 20 }}>
          {macDownloads.map((item) => {
            const entry = manifest ? manifest[item.manifestKey] : null;
            const href = (entry && entry.downloadUrl) || item.fallbackHref;
            return (
              <article key={item.manifestKey} style={cardStyle}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>{item.title}</h3>
                <p style={{ fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 18px', flex: 1 }}>
                  {item.description}
                </p>
                <div>
                  <a href={href} download style={buttonStyle('#111827')} onClick={track(href, item.manifestKey, true)}>
                    Download for Mac
                  </a>
                </div>
                <p style={smallText}>
                  {entry ? `${versionLine(entry)} · ` : ''}
                  {item.requirement} · <Link to={item.details} style={{ color: '#6b7280' }}>Details</Link>
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <footer style={{ textAlign: 'center', padding: '32px 24px', borderTop: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
          <Link to="/" style={{ color: '#6b7280', fontSize: '0.82rem', textDecoration: 'none' }}>Home</Link>
          <Link to="/hub" style={{ color: '#6b7280', fontSize: '0.82rem', textDecoration: 'none' }}>Hub</Link>
          <Link to="/docs" style={{ color: '#6b7280', fontSize: '0.82rem', textDecoration: 'none' }}>Docs</Link>
          <Link to="/privacy-policy" style={{ color: '#6b7280', fontSize: '0.82rem', textDecoration: 'none' }}>Privacy Policy</Link>
        </div>
      </footer>
    </div>
  );
}
